<div class="family-hub">
  <header class="family-hub__header">
    <div class="family-hub__header-icon" aria-hidden="true">
      <img src="/public/svgs/family-together.svg" alt="" width="22" height="22" />
    </div>
    <div class="family-hub__header-copy">
      <h1 class="family-hub__title">{{ 'app.familyHub.title' | translate }}</h1>
      <p class="family-hub__subtitle">{{ 'app.familyHub.subtitle' | translate }}</p>
    </div>
  </header>

  @if (isLoading()) {
    <p class="family-hub__hint" aria-live="polite">
      {{ 'app.familyHub.loading' | translate }}
    </p>
  } @else if (!family()) {
    <section class="family-hub__empty-state" role="status">
      <p class="family-hub__empty-title">
        {{ 'app.familyHub.unavailable' | translate }}
      </p>
    </section>
  } @else {
    @if (showSoftPrompt()) {
      <section class="family-hub__prompt" role="status">
        <div class="family-hub__prompt-copy">
          <p class="family-hub__prompt-title">
            {{ 'app.familyHub.softPrompt.title' | translate }}
          </p>
          <p class="family-hub__prompt-body">
            {{ 'app.familyHub.softPrompt.body' | translate }}
          </p>
        </div>
        <div class="family-hub__prompt-actions">
          <app-parent-button variant="text-ghost" (clicked)="dismissSoftPrompt()">
            {{ 'app.familyHub.softPrompt.dismiss' | translate }}
          </app-parent-button>
          @if (canInvite()) {
            <app-parent-button variant="ghost" (clicked)="openInviteAnyway()">
              {{ 'app.familyHub.softPrompt.inviteAnyway' | translate }}
            </app-parent-button>
          }
        </div>
      </section>
    }

    @if (showInviteRecoveryHint()) {
      <section class="family-hub__prompt family-hub__prompt--hint" role="note">
        <div class="family-hub__prompt-copy">
          <p class="family-hub__prompt-title">
            {{ 'app.familyHub.inviteRecovery.title' | translate }}
          </p>
          <p class="family-hub__prompt-body">
            {{ 'app.familyHub.inviteRecovery.body' | translate }}
          </p>
        </div>
      </section>
    }

    <section class="family-hub__section" aria-labelledby="family-hub-role-heading">
      <div class="family-hub__section-head">
        <h2 id="family-hub-role-heading" class="family-hub__section-title">
          {{ 'app.familyHub.yourRole' | translate }}
        </h2>
      </div>

      <div class="family-hub__panel">
        <label class="family-hub__field">
          <span class="family-hub__field-label">
            {{ 'app.familyHub.parentalFigure.label' | translate }}
          </span>
          <select
            class="family-hub__select"
            [ngModel]="myParentalFigure()"
            (ngModelChange)="onFigureChange($event)"
          >
            <option value="">
              {{ 'app.familyHub.parentalFigure.unset' | translate }}
            </option>
            @for (opt of figureOptions; track opt) {
              <option [value]="opt">
                {{ figureLabelKey(opt) | translate }}
              </option>
            }
          </select>
        </label>
      </div>
    </section>

    <section class="family-hub__section" aria-labelledby="family-hub-members-heading">
      <div class="family-hub__section-head">
        <div class="family-hub__section-head-main">
          <h2 id="family-hub-members-heading" class="family-hub__section-title">
            {{ 'app.familyHub.adults' | translate }}
          </h2>
          <span class="family-hub__count">{{ adults().length }}</span>
        </div>
        <div class="family-hub__section-head-actions">
          @if (canInvite()) {
            <app-parent-button variant="ghost" (clicked)="openInvite()">
              {{ 'app.familyHub.invite.cta' | translate }}
            </app-parent-button>
          }
          @if (canInviteExtended()) {
            <app-parent-button variant="ghost" (clicked)="openInviteExtended()">
              {{ 'app.familyHub.inviteExtended.cta' | translate }}
            </app-parent-button>
          } @else if (showExtendedUpgrade()) {
            <app-parent-button variant="text-ghost" (clicked)="goExtendedUpgrade()">
              {{ 'app.familyHub.inviteExtended.upgradeCta' | translate }}
            </app-parent-button>
          }
        </div>
      </div>

      <ul class="family-hub__list">
        @for (m of adults(); track m.id) {
          <li
            class="family-hub__row"
            [class.family-hub__row--me]="isMe(m.userId)"
          >
            <span class="family-hub__avatar" aria-hidden="true">
              {{ memberInitials(m.displayName, m.userId) }}
            </span>
            <div class="family-hub__row-copy">
              <span class="family-hub__name">
                {{ m.displayName || m.userId }}
                @if (isMe(m.userId)) {
                  <span class="family-hub__you">
                    {{ 'app.familyHub.you' | translate }}
                  </span>
                }
              </span>
              <span class="family-hub__figure">
                {{ figureLabelKey(m.parentalFigure) | translate }}
              </span>
            </div>
            <span
              class="family-hub__badge"
              [ngClass]="roleBadgeClass(m.role)"
            >
              {{ roleLabelKey(m.role) | translate }}
            </span>
            @if (
              (canRemoveCoParents() && m.role === 'CO_PARENT' && !isMe(m.userId)) ||
              (canLeaveFamily() && isMe(m.userId))
            ) {
              <div class="family-hub__member-actions">
                @if (canRemoveCoParents() && m.role === 'CO_PARENT' && !isMe(m.userId)) {
                  <app-parent-button
                    variant="text-ghost"
                    (clicked)="removeCoParent(m)"
                  >
                    {{ 'app.familyHub.removeCoParent' | translate }}
                  </app-parent-button>
                }
                @if (canLeaveFamily() && isMe(m.userId)) {
                  <app-parent-button
                    variant="text-ghost"
                    (clicked)="leaveFamily()"
                  >
                    {{ 'app.familyHub.leaveFamily' | translate }}
                  </app-parent-button>
                }
              </div>
            }
          </li>
        }
      </ul>
    </section>

    <section class="family-hub__section" aria-labelledby="family-hub-children-heading">
      <header class="family-hub__section-intro">
        <div class="family-hub__section-intro-copy">
          <h2 id="family-hub-children-heading" class="family-hub__section-title">
            {{ 'app.familyHub.children' | translate }}
          </h2>
          <p class="family-hub__section-subtitle">
            {{ 'app.familyHub.childrenDescription' | translate }}
          </p>
        </div>
        @if (children().length > childrenPreviewLimit) {
          <button
            type="button"
            class="family-hub__toggle"
            (click)="toggleShowAllChildren()"
          >
            @if (showAllChildren()) {
              {{ 'app.familyHub.showLessChildren' | translate }}
            } @else {
              {{
                'app.familyHub.showAllChildren'
                  | translate: { count: children().length }
              }}
            }
          </button>
        }
      </header>

      <div
        class="family-hub__kids"
        [class.family-hub__kids--expanded]="showAllChildren()"
      >
        @for (m of visibleChildren(); track m.userId) {
          <article class="family-hub__kid">
            <span class="family-hub__kid-avatar">
              @if (m.avatar) {
                <img
                  [src]="m.avatar"
                  [alt]="childDisplayName(m)"
                />
              } @else {
                <span>{{ { firstName: m.firstName, lastName: m.lastName } | initials }}</span>
              }
            </span>
            <div class="family-hub__kid-copy">
              <span class="family-hub__kid-name">{{ childDisplayName(m) }}</span>
              <span class="family-hub__kid-meta">
                {{
                  'app.pluralization.duties.' + (getDutiesCount(m) | pluralization)
                    | translate: { count: getDutiesCount(m) }
                }}
                <span class="family-hub__kid-separator" aria-hidden="true"></span>
                {{
                  'app.pluralization.tasks.' + (getTasksCount(m) | pluralization)
                    | translate: { count: getTasksCount(m) }
                }}
              </span>
            </div>
          </article>
        } @empty {
          <p class="family-hub__list-empty">
            {{ 'app.familyHub.noChildren' | translate }}
          </p>
        }
      </div>

      @if (canAddChild()) {
        <app-parent-button
          variant="ghost"
          [fullWidth]="true"
          (clicked)="openAddKidDialog()"
        >
          <span class="family-hub__add-child-icon" aria-hidden="true"></span>
          {{ 'app.familyHub.addChild' | translate }}
        </app-parent-button>
      } @else if (showKidsUpgrade()) {
        <p class="family-hub__hint">
          @if (kidsMaxSeatLimit(); as limit) {
            {{
              'app.familyHub.addChildUpgradeHint'
                | translate: { limit, count: children().length }
            }}
          } @else {
            {{ 'app.familyHub.addChildUpgradeHintGeneric' | translate }}
          }
        </p>
        <app-parent-button
          variant="text-ghost"
          [fullWidth]="true"
          (clicked)="goKidsUpgrade()"
        >
          {{ 'app.familyHub.addChildUpgradeCta' | translate }}
        </app-parent-button>
      }
    </section>

    @if (incoming().length) {
      <section
        class="family-hub__section family-hub__panel family-hub__panel--alert"
        aria-labelledby="family-hub-incoming-heading"
      >
        <div class="family-hub__section-head">
          <h2 id="family-hub-incoming-heading" class="family-hub__section-title">
            {{ 'app.familyHub.incomingTitle' | translate }}
          </h2>
          <span class="family-hub__pill">
            {{ incoming().length }}
          </span>
        </div>
        <ul class="family-hub__list">
          @for (inv of incoming(); track inv.id) {
            <li class="family-hub__row family-hub__row--invite">
              <span class="family-hub__avatar" aria-hidden="true">
                {{ memberInitials(inv.invitedByDisplayName, inv.invitedByUserId) }}
              </span>
              <div class="family-hub__row-copy">
                <span class="family-hub__name">{{ inv.invitedByDisplayName }}</span>
                <span class="family-hub__figure">
                  {{ 'app.familyHub.incomingHint' | translate }}
                </span>
              </div>
              <div class="family-hub__invite-actions">
                <app-parent-button variant="primary" (clicked)="acceptIncoming(inv)">
                  {{ 'app.familyHub.accept' | translate }}
                </app-parent-button>
                <app-parent-button variant="ghost" (clicked)="rejectIncoming(inv)">
                  {{ 'app.familyHub.reject' | translate }}
                </app-parent-button>
              </div>
            </li>
          }
        </ul>
      </section>
    }

    @if (outgoing().length) {
      <section class="family-hub__section" aria-labelledby="family-hub-outgoing-heading">
        <div class="family-hub__section-head">
          <h2 id="family-hub-outgoing-heading" class="family-hub__section-title">
            {{ 'app.familyHub.outgoingTitle' | translate }}
          </h2>
          <span class="family-hub__count">{{ outgoing().length }}</span>
        </div>
        <ul class="family-hub__list">
          @for (inv of outgoing(); track inv.id) {
            <li class="family-hub__row">
              <span class="family-hub__avatar family-hub__avatar--code" aria-hidden="true">
                #
              </span>
              <div class="family-hub__row-copy">
                <span class="family-hub__name">
                  {{ 'app.familyHub.outgoingItem' | translate: { code: inv.code } }}
                </span>
              </div>
              <span class="family-hub__badge family-hub__badge--pending">
                {{ inv.status }}
              </span>
            </li>
          }
        </ul>
      </section>
    }
  }
</div>
